'use client';

/**
 * ActiveFiltersCard — applied filter state, pinned above the facet stack.
 *
 * Load-bearing per the rail doctrine: applied constraints must never hide
 * inside a collapsed facet. Every active value shows as a removable chip; a
 * "clear all" drops them at once. Thin painter — chips are built from the store
 * filters × the facet payload (for readable value labels); it decides nothing.
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { X } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export interface ActiveChip {
  key: string; // facet key
  value: string; // raw value (SID for ref facets)
  label: string; // display label ("Grey", the vendor name)
}

export function ActiveFiltersCard({
  chips,
  onRemove,
  onClearAll,
  clearAllLabel,
}: {
  chips: ActiveChip[];
  onRemove: (key: string, value: string) => void;
  onClearAll: () => void;
  clearAllLabel: string;
}) {
  useCardExport(
    useMemo(() => ({
      label: 'Active filters',
      content_key: 'active_filters',
      surface: 'canvas' as const,
      status: 'loaded' as const,
      query_key: null,
      text: chips.map((c) => c.label).join(', '),
      json: { active: chips.map((c) => ({ key: c.key, value: c.value })) },
    }), [chips]),
  );

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 pb-1">
      {chips.map((c) => (
        <button
          key={`${c.key}:${c.value}`}
          onClick={() => onRemove(c.key, c.value)}
          className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs text-foreground hover:border-primary/60"
        >
          <span className="truncate">{c.label}</span>
          <X className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      ))}
      <button onClick={onClearAll} className={cn(text.muted, 'px-1 hover:text-foreground')}>
        {clearAllLabel}
      </button>
    </div>
  );
}
